import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { DataTableNew } from "@/components/shared/DataTable";
import SearchInput from "@/components/shared/SearchInput";
import DateRangeFilter, { dateRangeParams } from "@/components/shared/DateRangeFilter";
import { Card } from "@/components/ui/Card";
import MultiSelectFilter from "@/components/shared/MultiSelectFilter";
import { AuditDashboardService } from "@/services/auditDashboardService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/Button";
import DetailModal from "../components/DetailModal";

const LlmCallsTab = ({ tenantId }) => {
  const [search, setSearch] = useState("");
  const [feature, setFeature] = useState("");
  const [modelName, setModelName] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [filterOptions, setFilterOptions] = useState({ features: [], modelNames: [] });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    AuditDashboardService.fetchTenantLlmCallsFilterOptions(tenantId)
      .then((res) => setFilterOptions({ features: res?.features ?? [], modelNames: res?.modelNames ?? [] }))
      .catch((err) => console.warn("Failed to load LLM filter options", err));
  }, [tenantId]);

  const extraParams = useMemo(
    () => ({
      tenantId,
      ...(feature && { feature }),
      ...(modelName && { modelName }),
      ...dateRangeParams(range),
    }),
    [tenantId, feature, modelName, range]
  );

  const api = useCallback((params) => AuditDashboardService.fetchTenantLlmCalls(params), []);

  const handleView = useCallback(
    async (row) => {
      setDetailOpen(true);
      setDetailLoading(true);

      try {
        const res = await AuditDashboardService.fetchTenantLlmCallDetail(row.id, tenantId);
        setDetail(res?.data ?? null);
      } catch (err) {
        toast.error(apiErrorMessageFormat(err));
        setDetailOpen(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [tenantId]
  );

  const COLUMNS = useMemo(
  () => [
    {
      key: "createdAt",
      sortKey: "created_at",
      label: "Created at",
      sortable: true,
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      key: "feature",
      sortKey: "feature",
      label: "Feature",
      sortable: true,
    },
    {
      key: "modelName",
      sortKey: "model_name",
      label: "Model",
      sortable: true,
    },
    { key: "callLabel", label: "Call label" },
    {
      key: "inputTokens",
      label: "Tokens (in/out)",
      render: (v, row) => `${v ?? "—"} / ${row.outputTokens ?? "—"}`,
    },
    {
      key: "runId",
      label: "Run ID",
      render: (v) => (
        <span className="break-all whitespace-normal">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "view",
      label: "Actions",
      render: (_, row) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={(e) => {
            e.stopPropagation();
            handleView(row);
          }}
        >
          <Eye size={16} />
        </Button>
      ),
    },
  ],
  [handleView]
);

  if (!tenantId) {
    return <div className="p-6 text-sm text-text-primary/60">Pick a tenant above to view its LLM calls.</div>;
  }

  return (
    <div className="flex flex-col gap-2.5">
      <Card className="flex flex-wrap items-center gap-2 p-2.5">
        <SearchInput value={search} onChange={setSearch} placeholder="Search feature, label, model…" className="max-w-xs" />
        <MultiSelectFilter label="Feature" options={filterOptions.features} value={feature} onChange={setFeature} />
        <MultiSelectFilter label="Model" options={filterOptions.modelNames} value={modelName} onChange={setModelName} />
        <DateRangeFilter name="llm-calls" from={range.from} to={range.to} onChange={setRange} />
      </Card>

      <Card className="p-0 bg-table-heading border-border-primary">
        <DataTableNew
          columns={COLUMNS}
          api={api}
          search={search}
          extraParams={extraParams}
          defaultSort={{ key: "created_at", order: "desc" }}
          dataPath="items"
          paginationLabel="LLM calls"
        />
      </Card>

      <DetailModal
  open={detailOpen}
  onClose={() => setDetailOpen(false)}
  loading={detailLoading}
  title="LLM Call Details"
  fields={[
    { label: "Feature", value: detail?.feature },
    { label: "Model", value: detail?.modelName },
    { label: "Call Label", value: detail?.callLabel },
    { label: "Run ID", value: detail?.runId },
    { label: "Entity Type", value: detail?.entityType },
    { label: "Entity ID", value: detail?.entityId },
    { label: "Created By", value: detail?.createdByName || detail?.createdByEmail },
    {
      label: "Created At",
      value: detail?.createdAt
        ? new Date(detail.createdAt).toLocaleString()
        : "—",
    },
    { label: "Input Tokens", value: detail?.inputTokens },
    { label: "Output Tokens", value: detail?.outputTokens },
    { label: "Total Tokens", value: detail?.totalTokens },
    { label: "Processing Time", value: `${detail?.processingTimeS ?? "—"} s` },
    {
      label: "Payload Truncated",
      value: detail?.payloadTruncated ? "Yes" : "No",
    },
  ]}
  jsonSections={[
    { label: "Prompt", value: detail?.prompt },
    { label: "Raw Output", value: detail?.rawOutput },
    { label: "Extra Data", value: detail?.extraData },
  ]}
/>
    </div>
  );
};

export default LlmCallsTab;
